import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { api, ApiError } from '../lib/api.js';
import { Button } from '../ui/Button.js';
import { Field, Input, Select } from '../ui/Input.js';
import { Badge, Card, CardBody, CardHeader, SectionTitle, StatusDot } from '../ui/Card.js';
import { MultiSelect } from '../ui/MultiSelect.js';
import { LenderPipelines } from './pipeline/LenderPipelines.js';
import { useAuth, useModulePermission } from '../lib/auth-context.js';

interface PipelineResponse {
  total: number;
  page: number;
  pageSize: number;
  items: PipelineLoan[];
}

interface ContactLite {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  whatsappPhone?: string | null;
  contactType?: string;
}

interface PipelineLoan {
  id: string;
  loanNumber: string;
  loanOfficer: string | null;
  loanOfficerEmail: string | null;
  loanOfficerNMLS?: string | null;
  processorEmail: string | null;
  processorFullName: string | null;
  propertyAddress: string | null;
  loanStatus: string | null;
  lockStatus: string;
  lockExpired: string | null;
  lockExpirationDate: string | null;
  loanPurpose: string | null;
  outstandingCondition: number | null;
  lenderPortalUrl: string | null;
  titleCompanyEmail: string | null;
  assignedToContactId: string | null;
  assignedToContact: ContactLite | null;
  parties: Array<{ role: string; fullName: string; email: string | null; phone: string | null }>;
  // Closing-milestone fields (new)
  eSignCompletedFlag?: 'Y' | 'N' | string | null;
  estimatedClosingDate?: string | null;
  closingDateTime?: string | null;
  closingPackageReceivedDate?: string | null;
  disbursementDate?: string | null;
  epoDate?: string | null;
  disclosures?: { cdSignedFlag: 'Y' | 'N' | string | null } | null;
  // Server-derived signing status + pinned contacts (My Pipeline columns)
  initialDisclosuresSigned: boolean;
  initialCdSigned: boolean;
  initialCdSignedDate: string | null;
  extraContacts: ContactLite[];
}

interface Contact {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  whatsappPhone: string | null;
  contactType: string;
}

type PipelineTab = 'in_progress' | 'other' | 'all';

export function PipelinePage() {
  const perm = useModulePermission('pipeline_tracking');
  const { me } = useAuth();
  const isAdmin = me?.user.role === 'admin' || me?.user.role === 'super_admin';
  // My Pipeline tabs. "in_progress" and "other" are both NMLS-scoped (scope
  // 'my'); the admin-only "all" tab is org-wide with no bucket filter.
  const [tab, setTab] = useState<PipelineTab>('in_progress');
  const scope: 'my' | 'all' = tab === 'all' ? 'all' : 'my';
  const bucket: 'in_progress' | 'other' | undefined = tab === 'all' ? undefined : tab;
  const [q, setQ] = useState('');
  const [loanStatuses, setLoanStatuses] = useState<string[]>([]);
  const [lockStatuses, setLockStatuses] = useState<string[]>([]);
  const [purposes, setPurposes] = useState<string[]>([]);
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [lockExpiringDays, setLockExpiringDays] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [activeLoanId, setActiveLoanId] = useState<string | null>(null);
  const [showNewLoan, setShowNewLoan] = useState(false);
  // Click-to-email compose window (Contacts column). Sends via the logged-in
  // MLO's configured Gmail SMTP through /api/reach/email.
  const [compose, setCompose] = useState<{
    loanId: string;
    to: string;
    toName: string;
    subject: string;
  } | null>(null);
  // "Add contacts" picker target loan (Contacts column, max 2 extra).
  const [addContactsFor, setAddContactsFor] = useState<PipelineLoan | null>(null);
  // Loan whose live UWM conditions are being viewed (conditions-count click).
  const [conditionsLoan, setConditionsLoan] = useState<PipelineLoan | null>(null);

  // Stable CSV serialization for stable React Query cache keys + URL params.
  const loanStatusCsv = loanStatuses.slice().sort().join(',');
  const lockStatusCsv = lockStatuses.slice().sort().join(',');
  const purposeCsv = purposes.slice().sort().join(',');

  // Reset to page 1 whenever a filter (incl. tab) changes so we don't get
  // stranded on page N when the filter shortens results below N pages.
  const filterKey = `${tab}|${q}|${loanStatusCsv}|${lockStatusCsv}|${purposeCsv}|${outstandingOnly}|${lockExpiringDays}|${pageSize}`;
  useEffect(() => {
    setPage(1);
  }, [filterKey]);

  const params = new URLSearchParams();
  params.set('scope', scope);
  if (bucket) params.set('bucket', bucket);
  if (q) params.set('q', q);
  if (loanStatusCsv) params.set('loanStatus', loanStatusCsv);
  if (lockStatusCsv) params.set('lockStatus', lockStatusCsv);
  if (purposeCsv) params.set('loanPurpose', purposeCsv);
  if (outstandingOnly) params.set('outstandingConditionsGtZero', 'true');
  if (lockExpiringDays) params.set('lockExpiringWithinDays', lockExpiringDays);
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));

  const query = useQuery<PipelineResponse>({
    queryKey: ['pipeline', params.toString()],
    queryFn: () => api<PipelineResponse>(`/api/pipeline/loans?${params.toString()}`),
  });

  const contactsQuery = useQuery<{ contacts: Contact[] }>({
    queryKey: ['pipeline-contacts'],
    queryFn: () => api<{ contacts: Contact[] }>('/api/pipeline/contacts'),
  });

  const statsQuery = useQuery<{
    total: number;
    locked: number;
    expiringSoon: number;
    withConditions: number;
    scope: 'my' | 'all';
    hasNmls: boolean;
  }>({
    queryKey: ['pipeline-stats', tab],
    queryFn: () =>
      api<{
        total: number;
        locked: number;
        expiringSoon: number;
        withConditions: number;
        scope: 'my' | 'all';
        hasNmls: boolean;
      }>(`/api/pipeline/stats?scope=${scope}${bucket ? `&bucket=${bucket}` : ''}`),
    refetchInterval: 30_000,
  });

  const facetsQuery = useQuery<{
    statuses: Array<{ value: string; count: number }>;
    purposes: Array<{ value: string; count: number }>;
  }>({
    queryKey: ['pipeline-facets', tab],
    queryFn: () =>
      api<{
        statuses: Array<{ value: string; count: number }>;
        purposes: Array<{ value: string; count: number }>;
      }>(`/api/pipeline/facets?scope=${scope}${bucket ? `&bucket=${bucket}` : ''}`),
    refetchInterval: 60_000,
  });

  const qc = useQueryClient();

  // Build the prefilled subject and open the compose window for a contact.
  const openCompose = useCallback(
    (loan: PipelineLoan, to: string, toName: string) => {
      const borrower = loan.parties.find((pt) => pt.role === 'borrower1')?.fullName ?? '';
      const subject = [
        `Loan #${loan.loanNumber}`,
        borrower,
        loan.propertyAddress ?? '',
      ]
        .filter((s) => s && s.trim())
        .join(' — ');
      setCompose({ loanId: loan.id, to, toName, subject });
    },
    [],
  );

  // Persist up to 2 pinned extra contacts on a loan.
  const extraContactsMutation = useMutation({
    mutationFn: (args: { loanId: string; contactIds: string[] }) =>
      api(`/api/pipeline/loans/${args.loanId}/extra-contacts`, {
        method: 'PATCH',
        body: JSON.stringify({ contactIds: args.contactIds }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipeline'] });
      setAddContactsFor(null);
    },
  });

  /**
   * Sync status — polls /api/sync/jobs every 5s. Returns
   *   { jobs: SyncJob[]; state: { lastSuccessfulSyncAt: string | null; ... } }
   * Used to render the "Last sync at … " line + an in-flight chip.
   */
  const syncStatusQuery = useQuery<{
    jobs: Array<{
      id: string;
      jobType: string;
      status: string;
      recordsFetched: number;
      recordsChanged: number;
      errorSummary: string | null;
      startedAt: string | null;
      completedAt: string | null;
      createdAt: string;
    }>;
    state: { lastSuccessfulSyncAt: string | null } | null;
  }>({
    queryKey: ['pipeline-sync-status'],
    queryFn: () => api('/api/sync/jobs'),
    refetchInterval: 5_000,
  });
  const inFlightJob = syncStatusQuery.data?.jobs.find(
    (j) => j.status === 'queued' || j.status === 'running',
  );
  const mostRecentCompleted = syncStatusQuery.data?.jobs.find(
    (j) => j.status === 'success' || j.status === 'failed',
  );
  const lastSyncAt = syncStatusQuery.data?.state?.lastSuccessfulSyncAt ?? null;

  const invalidateAllSyncCaches = () => {
    qc.invalidateQueries({ queryKey: ['pipeline'] });
    qc.invalidateQueries({ queryKey: ['pipeline-stats'] });
    qc.invalidateQueries({ queryKey: ['pipeline-facets'] });
    qc.invalidateQueries({ queryKey: ['pipeline-sync-status'] });
  };

  const deltaMutation = useMutation({
    mutationFn: () => api<{ jobId: string }>('/api/sync/uwm/delta', { method: 'POST', body: '{}' }),
    onSuccess: invalidateAllSyncCaches,
  });
  const fullMutation = useMutation({
    mutationFn: () => api<{ jobId: string }>('/api/sync/uwm/full', { method: 'POST', body: '{}' }),
    onSuccess: invalidateAllSyncCaches,
  });

  const activeLoan = useMemo(
    () => query.data?.items.find((l) => l.id === activeLoanId) ?? null,
    [query.data, activeLoanId],
  );

  const closeDrawer = useCallback(() => setActiveLoanId(null), []);

  const total = query.data?.total ?? 0;
  // KPIs come from the server-side /stats endpoint so they reflect every loan
  // in the org, not just the rows on the current page.
  const stats = statsQuery.data;
  const lockedCount = stats?.locked ?? 0;
  const expiringCount = stats?.expiringSoon ?? 0;
  const conditionsCount = stats?.withConditions ?? 0;
  const totalCount = stats?.total ?? total;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-gold-700">
            My Pipeline
          </p>
          <h1 className="mt-1 font-display text-3xl font-bold text-brand-navy-800">
            My Pipeline
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Live view of UWM loans synced through the Insights API.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-2">
            {isAdmin && (
              <Button
                variant="secondary"
                onClick={() => setShowNewLoan(true)}
                title="Manually add a loan (admin only). UWM-sourced rows are normally created by sync."
              >
                + New loan
              </Button>
            )}
            <Button
              variant="secondary"
              disabled={!perm.canEdit || deltaMutation.isPending}
              onClick={() => deltaMutation.mutate()}
              title="Pull loans modified since the last successful sync (incremental)."
            >
              {deltaMutation.isPending ? (
                <span className="inline-flex items-center gap-1.5">
                  <Spinner /> Queuing…
                </span>
              ) : (
                'Run delta sync'
              )}
            </Button>
            <Button
              variant="gold"
              disabled={!perm.canAdmin || fullMutation.isPending}
              onClick={() => fullMutation.mutate()}
              title="Pull every loan modified in the last 12 months, any status."
            >
              {fullMutation.isPending ? (
                <span className="inline-flex items-center gap-1.5">
                  <Spinner /> Queuing…
                </span>
              ) : (
                'Run full sync'
              )}
            </Button>
          </div>
          <SyncStatusLine
            lastSyncAt={lastSyncAt}
            inFlight={inFlightJob ?? null}
            mostRecentCompleted={mostRecentCompleted ?? null}
          />
        </div>
      </header>

      {/* Sub-section tabs — both NMLS-scoped; admin All-loans is org-wide. */}
      <div className="flex items-center justify-between border-b border-slate-200">
        <div className="flex gap-1">
          <ScopeTab active={tab === 'in_progress'} onClick={() => setTab('in_progress')}>
            In Progress
            {tab === 'in_progress' && statsQuery.data && (
              <span className="ml-2 rounded-full bg-brand-navy-100 px-1.5 py-0.5 text-[10px] font-semibold text-brand-navy-700">
                {statsQuery.data.total.toLocaleString()}
              </span>
            )}
          </ScopeTab>
          <ScopeTab active={tab === 'other'} onClick={() => setTab('other')}>
            Other
            {tab === 'other' && statsQuery.data && (
              <span className="ml-2 rounded-full bg-brand-navy-100 px-1.5 py-0.5 text-[10px] font-semibold text-brand-navy-700">
                {statsQuery.data.total.toLocaleString()}
              </span>
            )}
          </ScopeTab>
          {isAdmin && (
            <ScopeTab active={tab === 'all'} onClick={() => setTab('all')}>
              All loans
              <span className="ml-1.5 rounded-full bg-brand-gold-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-gold-700">
                Admin
              </span>
              {tab === 'all' && statsQuery.data && (
                <span className="ml-2 rounded-full bg-brand-navy-100 px-1.5 py-0.5 text-[10px] font-semibold text-brand-navy-700">
                  {statsQuery.data.total.toLocaleString()}
                </span>
              )}
            </ScopeTab>
          )}
        </div>
        {scope === 'my' && me?.user.nmls && (
          <span className="pb-2 text-[11px] text-slate-500">
            Filtering by NMLS <strong className="font-mono text-brand-navy-700">{me.user.nmls}</strong>
          </span>
        )}
      </div>

      {/* No-NMLS banner — both NMLS-scoped tabs need an NMLS to show anything. */}
      {scope === 'my' && statsQuery.data && !statsQuery.data.hasNmls && (
        <NoNmlsBanner />
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard label="Total loans" value={totalCount} tone="info" />
        <KpiCard label="Locked" value={lockedCount} tone="success" />
        <KpiCard label="Lock expiring ≤ 7 days" value={expiringCount} tone="warning" />
        <KpiCard label="Outstanding conditions" value={conditionsCount} tone="gold" />
      </div>

      {/* Filters */}
      <Card>
        <CardHeader title="Filter" subtitle="Search across loan, borrower, address, officer, status." />
        <CardBody>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
            <Field label="Search">
              <Input
                placeholder="loan #, borrower, address…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </Field>
            <Field label="Loan status">
              <MultiSelect
                placeholder="All statuses"
                options={(facetsQuery.data?.statuses ?? []).map((s) => ({
                  value: s.value,
                  count: s.count,
                }))}
                value={loanStatuses}
                onChange={setLoanStatuses}
              />
            </Field>
            <Field label="Lock status">
              <MultiSelect
                placeholder="Any"
                options={[
                  { value: 'Locked' },
                  { value: 'Not Locked' },
                  { value: 'Unknown' },
                ]}
                value={lockStatuses}
                onChange={setLockStatuses}
              />
            </Field>
            <Field label="Purpose">
              <MultiSelect
                placeholder="Any"
                options={
                  facetsQuery.data?.purposes && facetsQuery.data.purposes.length > 0
                    ? facetsQuery.data.purposes.map((p) => ({ value: p.value, count: p.count }))
                    : [{ value: 'Purchase' }, { value: 'Refinance' }]
                }
                value={purposes}
                onChange={setPurposes}
              />
            </Field>
            <Field label="Lock expiring (days)">
              <Input
                type="number"
                min={0}
                placeholder="7"
                value={lockExpiringDays}
                onChange={(e) => setLockExpiringDays(e.target.value)}
              />
            </Field>
            <Field label="Outstanding conditions">
              <label className="mt-1 flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={outstandingOnly}
                  onChange={(e) => setOutstandingOnly(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-brand-navy-600 focus:ring-brand-navy-300"
                />
                Only with conditions &gt; 0
              </label>
            </Field>
          </div>
        </CardBody>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader
          title={`Loans (${total})`}
          subtitle="Click any row to open the loan detail drawer."
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-2.5">Loan #</th>
                <th className="px-4 py-2.5">Borrower</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Lock</th>
                <th className="px-4 py-2.5">Lock Exp.</th>
                <th className="px-4 py-2.5">Cond.</th>
                <th className="px-4 py-2.5">Initial disclosures signed</th>
                <th className="px-4 py-2.5">Initial CD signed</th>
                <th className="px-4 py-2.5">Contacts</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {query.isLoading && (
                <tr>
                  <td colSpan={10} className="px-4 py-6 text-center text-sm text-slate-500">
                    Loading pipeline…
                  </td>
                </tr>
              )}
              {!query.isLoading && query.data?.items.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-6 text-center text-sm text-slate-500">
                    No loans match the current filters.
                  </td>
                </tr>
              )}
              {query.data?.items.map((l) => {
                const b1 = l.parties.find((p) => p.role === 'borrower1')?.fullName ?? '';
                const expDays = l.lockExpirationDate
                  ? Math.round(
                      (new Date(l.lockExpirationDate).getTime() - Date.now()) / 86400000,
                    )
                  : null;
                return (
                  <tr
                    key={l.id}
                    className="cursor-pointer border-b border-slate-100 table-row-hover"
                    onClick={() => setActiveLoanId(l.id)}
                  >
                    <td className="px-4 py-2.5 font-mono text-[13px] font-semibold text-brand-navy-700">
                      {l.loanNumber}
                    </td>
                    <td className="px-4 py-2.5">{b1 || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-2.5">
                      {l.loanStatus ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const s = l.loanStatus!;
                            setLoanStatuses((cur) =>
                              cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s],
                            );
                          }}
                          title={`Toggle filter: ${l.loanStatus}`}
                          className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy-300"
                        >
                          <Badge tone={statusTone(l.loanStatus)}>{l.loanStatus}</Badge>
                        </button>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-1.5 text-slate-700">
                        <StatusDot
                          tone={
                            l.lockStatus === 'Locked'
                              ? 'success'
                              : l.lockStatus === 'Not Locked'
                                ? 'danger'
                                : 'neutral'
                          }
                        />
                        {l.lockStatus}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-700">
                      {l.lockExpirationDate ? (
                        <span className={expDays !== null && expDays <= 7 ? 'text-amber-700 font-medium' : ''}>
                          {new Date(l.lockExpirationDate).toLocaleDateString()}
                          {expDays !== null && expDays >= 0 && (
                            <span className="ml-1 text-xs text-slate-400">({expDays}d)</span>
                          )}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {l.outstandingCondition === null || l.outstandingCondition === undefined ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <button
                          type="button"
                          title="View live underwriting conditions from UWM"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConditionsLoan(l);
                          }}
                          className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy-300"
                        >
                          <Badge tone={l.outstandingCondition > 0 ? 'warning' : 'success'}>
                            {l.outstandingCondition}
                          </Badge>
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge tone={l.initialDisclosuresSigned ? 'success' : 'neutral'}>
                        {l.initialDisclosuresSigned ? 'Yes' : 'No'}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5">
                      {l.initialCdSigned ? (
                        <div className="leading-tight">
                          <Badge tone="success">Yes</Badge>
                          {l.initialCdSignedDate && (
                            <div className="mt-0.5 text-[11px] text-slate-500">
                              {new Date(l.initialCdSignedDate).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      ) : (
                        <Badge tone="neutral">No</Badge>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <ContactsCell
                        loan={l}
                        canEdit={perm.canEdit}
                        onEmail={(to, name) => openCompose(l, to, name)}
                        onAdd={() => setAddContactsFor(l)}
                      />
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {l.lenderPortalUrl && (
                        <a
                          href={l.lenderPortalUrl}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs font-medium text-brand-navy-700 hover:text-brand-navy-900 hover:underline"
                        >
                          Portal ↗
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <PipelineFooter
          page={page}
          pageSize={pageSize}
          total={total}
          loading={query.isFetching}
          onPage={setPage}
          onPageSize={setPageSize}
        />
      </Card>

      {/* The other lenders' pipelines, read from their portals every hour. */}
      <LenderPipelines canEdit={perm.canEdit} />

      {activeLoan && (
        <LoanDrawerWrapper
          loan={activeLoan}
          contacts={contactsQuery.data?.contacts}
          canEdit={perm.canEdit}
          isAdmin={isAdmin}
          onClose={closeDrawer}
          onAfterDelete={() => {
            closeDrawer();
            invalidateAllSyncCaches();
          }}
        />
      )}

      {showNewLoan && (
        <NewLoanModal
          onClose={() => setShowNewLoan(false)}
          onCreated={() => {
            setShowNewLoan(false);
            invalidateAllSyncCaches();
          }}
        />
      )}

      {compose && (
        <ComposeEmailModal
          loanId={compose.loanId}
          to={compose.to}
          toName={compose.toName}
          initialSubject={compose.subject}
          onClose={() => setCompose(null)}
        />
      )}

      {addContactsFor && (
        <AddContactsModal
          loan={addContactsFor}
          contacts={(contactsQuery.data?.contacts ?? []).filter((c) => !!c.email)}
          isSaving={extraContactsMutation.isPending}
          onSave={(contactIds) =>
            extraContactsMutation.mutate({ loanId: addContactsFor.id, contactIds })
          }
          onClose={() => setAddContactsFor(null)}
        />
      )}

      {conditionsLoan && (
        <ConditionsModal loan={conditionsLoan} onClose={() => setConditionsLoan(null)} />
      )}
    </div>
  );
}

// ===========================================================================
// Conditions modal — live underwriting conditions pulled from UWM's View Loan
// Conditions API (separate from Insights). Shows the loan's Sr UW + UW2
// contacts for context, then outstanding conditions grouped by process stage,
// then cleared conditions.
// ===========================================================================

interface ConditionItem {
  id: string;
  conditionCategory: string | null;
  conditionDescription: string | null;
  isConditionUploaded: boolean;
  conditionSatisfactionDate: string | null;
}
interface ConditionGroup {
  key: string;
  countItems: number;
  uploadedConditionsCount: number;
  conditions: ConditionItem[];
}
interface UwContact {
  name: string | null;
  email: string | null;
  ext: string | null;
}
interface ConditionsResponse {
  loanNumber: string;
  underwriters: { seniorUw: UwContact; uw2: UwContact };
  outstandingCount: number;
  conditionGroups: ConditionGroup[];
  clearedConditions: ConditionItem[];
}

function ConditionsModal({ loan, onClose }: { loan: PipelineLoan; onClose: () => void }) {
  const q = useQuery<ConditionsResponse>({
    queryKey: ['loan-conditions', loan.id],
    queryFn: () => api<ConditionsResponse>(`/api/pipeline/loans/${loan.id}/conditions`),
    retry: false,
    staleTime: 30_000,
  });

  const errBody =
    q.error instanceof ApiError ? (q.error.body as { message?: string; error?: string } | null) : null;
  const errMsg = errBody?.message ?? (q.error instanceof Error ? q.error.message : null);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={onClose} aria-hidden />
      <aside className="fixed inset-y-0 right-0 z-50 flex h-full w-full max-w-xl flex-col overflow-y-auto bg-white shadow-floating">
        <header className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-200 bg-white px-6 py-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-gold-700">
              Underwriting conditions
            </p>
            <div className="font-display text-2xl font-bold text-brand-navy-800">
              Loan #{loan.loanNumber}
            </div>
            <p className="text-xs text-slate-500">Live from UWM View Loan Conditions API</p>
          </div>
          <Button variant="ghost" onClick={onClose}>
            ✕ Close
          </Button>
        </header>

        <div className="space-y-5 px-6 py-5">
          {/* Underwriters (from Insights — context for whose conditions these are) */}
          <section>
            <SectionTitle>Underwriters on this loan</SectionTitle>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <UwCard label="Senior UW" uw={q.data?.underwriters.seniorUw ?? null} />
              <UwCard label="Underwriter 2" uw={q.data?.underwriters.uw2 ?? null} />
            </div>
            <p className="mt-1 text-[11px] text-slate-400">
              Conditions are grouped by UWM stage below. Senior Underwriter and
              Underwriter II groups are shown first.
            </p>
          </section>

          {q.isLoading && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
              Loading live conditions from UWM…
            </div>
          )}

          {q.isError && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
              <div className="font-semibold">Couldn&apos;t load conditions</div>
              <p className="mt-0.5 text-xs">{errMsg ?? 'Request failed.'}</p>
              {errBody?.error === 'rate_limited' && (
                <Button size="sm" variant="secondary" className="mt-2" onClick={() => q.refetch()}>
                  Retry
                </Button>
              )}
            </div>
          )}

          {q.data && (
            <>
              {/* Outstanding */}
              <section>
                <SectionTitle>
                  Outstanding conditions ({q.data.outstandingCount})
                </SectionTitle>
                {q.data.conditionGroups.length === 0 ? (
                  <p className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                    No outstanding conditions. 🎉
                  </p>
                ) : (
                  <div className="mt-2 space-y-3">
                    {[...q.data.conditionGroups].sort(byUwFirst).map((g) => {
                      const isUw = uwRank(g.key) < 2;
                      return (
                      <div
                        key={g.key}
                        className={clsx(
                          'rounded-lg border',
                          isUw ? 'border-brand-navy-300' : 'border-slate-200',
                        )}
                      >
                        <div
                          className={clsx(
                            'flex items-center justify-between border-b px-3 py-1.5',
                            isUw
                              ? 'border-brand-navy-200 bg-brand-navy-50'
                              : 'border-slate-100 bg-slate-50',
                          )}
                        >
                          <span className="text-xs font-semibold uppercase tracking-wider text-brand-navy-700">
                            {g.key}
                          </span>
                          <Badge tone="warning">{g.conditions.length}</Badge>
                        </div>
                        <ul className="divide-y divide-slate-100">
                          {g.conditions.map((c) => (
                            <li key={c.id} className="px-3 py-2">
                              <div className="flex items-start justify-between gap-2">
                                <span className="text-sm text-slate-800">
                                  {c.conditionDescription || '(no description)'}
                                </span>
                                {c.conditionCategory && (
                                  <Badge tone="neutral" className="shrink-0">
                                    {c.conditionCategory}
                                  </Badge>
                                )}
                              </div>
                              {c.isConditionUploaded && (
                                <div className="mt-1 text-[11px] text-brand-navy-500">
                                  Documents uploaded — pending UW review
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

/** Rank condition group keys so Senior Underwriter + Underwriter II surface first. */
function uwRank(key: string): number {
  const k = key.toLowerCase();
  if (k.includes('senior underwriter') || k.includes('senior uw') || k.includes('sr uw')) return 0;
  if (k.includes('underwriter ii') || k.includes('underwriter 2') || k.includes('uw2')) return 1;
  return 2;
}
function byUwFirst(a: ConditionGroup, b: ConditionGroup): number {
  return uwRank(a.key) - uwRank(b.key);
}

function UwCard({ label, uw }: { label: string; uw: UwContact | null }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        {label}
      </div>
      {uw?.name ? (
        <>
          <div className="text-sm font-medium text-brand-navy-800">{uw.name}</div>
          {uw.email && (
            <a
              href={`mailto:${uw.email}`}
              className="text-xs text-brand-navy-600 hover:underline"
            >
              {uw.email}
            </a>
          )}
          {uw.ext && <div className="text-[11px] text-slate-400">ext. {uw.ext}</div>}
        </>
      ) : (
        <div className="text-sm text-slate-400">Not assigned</div>
      )}
    </div>
  );
}

// ===========================================================================
// Contacts column cell — MLO + Processor (from UWM) + up to 2 pinned org
// contacts, each click-to-email. Names with an email become buttons that open
// the compose window; names without an email render muted.
// ===========================================================================

function ContactsCell({
  loan,
  canEdit,
  onEmail,
  onAdd,
}: {
  loan: PipelineLoan;
  canEdit: boolean;
  onEmail: (to: string, name: string) => void;
  onAdd: () => void;
}) {
  const rows: Array<{ key: string; name: string; email: string | null; label: string }> = [];
  if (loan.loanOfficer || loan.loanOfficerEmail) {
    rows.push({
      key: 'mlo',
      name: loan.loanOfficer ?? 'MLO',
      email: loan.loanOfficerEmail,
      label: 'MLO',
    });
  }
  if (loan.processorFullName || loan.processorEmail) {
    rows.push({
      key: 'processor',
      name: loan.processorFullName ?? 'Processor',
      email: loan.processorEmail,
      label: 'Processor',
    });
  }
  for (const c of loan.extraContacts ?? []) {
    rows.push({ key: `extra-${c.id}`, name: c.name, email: c.email, label: 'Contact' });
  }

  return (
    <div className="flex flex-col gap-0.5">
      {rows.length === 0 && <span className="text-slate-400">—</span>}
      {rows.map((r) =>
        r.email ? (
          <button
            key={r.key}
            type="button"
            title={`Email ${r.name} <${r.email}>`}
            onClick={(e) => {
              e.stopPropagation();
              onEmail(r.email!, r.name);
            }}
            className="group flex items-baseline gap-1.5 text-left"
          >
            <span className="text-[9px] uppercase tracking-wider text-slate-400">
              {r.label}
            </span>
            <span className="text-sm text-brand-navy-700 group-hover:underline">
              {r.name}
            </span>
          </button>
        ) : (
          <span key={r.key} className="flex items-baseline gap-1.5">
            <span className="text-[9px] uppercase tracking-wider text-slate-400">
              {r.label}
            </span>
            <span className="text-sm text-slate-500" title="No email on file">
              {r.name}
            </span>
          </span>
        ),
      )}
      {canEdit && (loan.extraContacts?.length ?? 0) < 2 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAdd();
          }}
          className="mt-0.5 self-start text-[11px] font-medium text-brand-gold-700 hover:underline"
        >
          + Add contact
        </button>
      )}
    </div>
  );
}

// ===========================================================================
// Compose-email window — opens from a Contacts-column name. Sends via the
// logged-in MLO's configured Gmail SMTP (/api/reach/email), prefilled subject.
// ===========================================================================

function ComposeEmailModal({
  loanId,
  to,
  toName,
  initialSubject,
  onClose,
}: {
  loanId: string;
  to: string;
  toName: string;
  initialSubject: string;
  onClose: () => void;
}) {
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState('');
  const [result, setResult] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const send = useMutation({
    mutationFn: () =>
      api('/api/reach/email', {
        method: 'POST',
        body: JSON.stringify({ loanId, to, subject, text: body || ' ' }),
      }),
    onSuccess: () => setResult({ tone: 'ok', text: 'Email sent.' }),
    onError: (e: Error) => setResult({ tone: 'err', text: e.message }),
  });

  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={onClose} aria-hidden />
      <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-5 shadow-floating">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-gold-700">
              New email
            </p>
            <h3 className="font-display text-lg font-bold text-brand-navy-800">
              To {toName}
            </h3>
            <p className="text-xs text-slate-500">{to}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            ✕
          </Button>
        </div>
        <p className="mt-1 text-[11px] text-slate-500">
          Sends through your configured Gmail SMTP (Integrations tab) and is logged on the loan.
        </p>
        <div className="mt-3 space-y-3">
          <Field label="Subject">
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Field>
          <Field label="Message">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              placeholder="Write your message…"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
            />
          </Field>
          {result && (
            <div
              className={clsx(
                'rounded-lg px-3 py-2 text-xs ring-1',
                result.tone === 'ok'
                  ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                  : 'bg-rose-50 text-rose-700 ring-rose-200',
              )}
            >
              {result.text}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              {result?.tone === 'ok' ? 'Close' : 'Cancel'}
            </Button>
            <Button
              disabled={send.isPending || !subject.trim() || result?.tone === 'ok'}
              onClick={() => {
                setResult(null);
                send.mutate();
              }}
            >
              {send.isPending ? 'Sending…' : 'Send email'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

// ===========================================================================
// Add-contacts modal — pin up to 2 org contacts (with email) to a loan's
// Contacts column. Pre-selects whatever is already pinned.
// ===========================================================================

function AddContactsModal({
  loan,
  contacts,
  isSaving,
  onSave,
  onClose,
}: {
  loan: PipelineLoan;
  contacts: Contact[];
  isSaving: boolean;
  onSave: (contactIds: string[]) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<string[]>(
    (loan.extraContacts ?? []).map((c) => c.id),
  );
  const [search, setSearch] = useState('');
  const toggle = (id: string) => {
    setSelected((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= 2) return cur; // cap at 2
      return [...cur, id];
    });
  };
  const filtered = contacts.filter((c) =>
    `${c.name} ${c.email ?? ''}`.toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={onClose} aria-hidden />
      <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-5 shadow-floating">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-display text-lg font-bold text-brand-navy-800">
              Add contacts to loan #{loan.loanNumber}
            </h3>
            <p className="text-xs text-slate-500">
              Pick up to 2 contacts (with an email) for quick click-to-email. {selected.length}/2
              selected.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            ✕
          </Button>
        </div>
        <div className="mt-3">
          <Input
            placeholder="Search contacts…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-slate-200">
          {filtered.length === 0 && (
            <p className="px-3 py-6 text-center text-xs text-slate-500">
              No contacts with an email match. Add contacts (with emails) on the Administration page.
            </p>
          )}
          {filtered.map((c) => {
            const checked = selected.includes(c.id);
            const disabled = !checked && selected.length >= 2;
            return (
              <label
                key={c.id}
                className={clsx(
                  'flex cursor-pointer items-center gap-3 border-b border-slate-100 px-3 py-2 last:border-b-0',
                  disabled ? 'opacity-40' : 'hover:bg-brand-cream-100',
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggle(c.id)}
                  className="h-4 w-4 rounded border-slate-300 text-brand-navy-600"
                />
                <span className="flex-1">
                  <span className="block text-sm text-brand-navy-800">{c.name}</span>
                  <span className="block text-xs text-slate-500">{c.email}</span>
                </span>
                <Badge tone="neutral">{c.contactType}</Badge>
              </label>
            );
          })}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={() => onSave(selected)}>
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </>
  );
}

/**
 * Thin wrapper that memoizes the empty-contacts fallback so the prop
 * reference passed to React.memo'd LoanDrawer is stable.
 */
const EMPTY_CONTACTS: Contact[] = [];
function LoanDrawerWrapper({
  loan,
  contacts,
  canEdit,
  isAdmin,
  onClose,
  onAfterDelete,
}: {
  loan: PipelineLoan;
  contacts: Contact[] | undefined;
  canEdit: boolean;
  isAdmin: boolean;
  onClose: () => void;
  onAfterDelete: () => void;
}) {
  return (
    <LoanDrawer
      loan={loan}
      contacts={contacts ?? EMPTY_CONTACTS}
      canEdit={canEdit}
      isAdmin={isAdmin}
      onClose={onClose}
      onAfterDelete={onAfterDelete}
    />
  );
}

/**
 * Small spinner used inside the sync buttons while the POST is in-flight.
 * Pure SVG — no extra deps.
 */
function Spinner() {
  return (
    <svg
      className="h-3.5 w-3.5 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface SyncJobLite {
  id: string;
  jobType: string;
  status: string;
  recordsFetched: number;
  recordsChanged: number;
  errorSummary: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

/** "Last sync …" + current-job chip rendered under the sync buttons. */
function SyncStatusLine({
  lastSyncAt,
  inFlight,
  mostRecentCompleted,
}: {
  lastSyncAt: string | null;
  inFlight: SyncJobLite | null;
  mostRecentCompleted: SyncJobLite | null;
}) {
  // Show the in-flight job first; otherwise the most recent completed one.
  const ago = (iso: string | null): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    const ms = Date.now() - d.getTime();
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return d.toLocaleString();
  };

  return (
    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-[11px] text-slate-500">
      {inFlight ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-navy-50 px-2 py-0.5 font-medium text-brand-navy-700">
          <Spinner />
          {inFlight.status === 'queued' ? 'Queued' : 'Running'} ·{' '}
          {inFlight.jobType.replace('_', ' ')}
        </span>
      ) : mostRecentCompleted ? (
        <span
          className={
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ' +
            (mostRecentCompleted.status === 'success'
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-rose-50 text-rose-700')
          }
          title={mostRecentCompleted.errorSummary ?? ''}
        >
          {mostRecentCompleted.status === 'success' ? '✓' : '✕'}{' '}
          {mostRecentCompleted.jobType.replace('_', ' ')} ·{' '}
          {mostRecentCompleted.recordsFetched} fetched ·{' '}
          {mostRecentCompleted.recordsChanged} changed
        </span>
      ) : null}
      <span>
        Last successful sync:{' '}
        <strong className="text-brand-navy-700" title={lastSyncAt ?? undefined}>
          {lastSyncAt ? ago(lastSyncAt) : 'never'}
        </strong>
      </span>
    </div>
  );
}

function ScopeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        '-mb-px flex items-center border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
        active
          ? 'border-brand-navy-700 text-brand-navy-800'
          : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-900',
      )}
    >
      {children}
    </button>
  );
}

function NoNmlsBanner() {
  const qc = useQueryClient();
  const [nmls, setNmls] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api('/api/me', {
        method: 'PUT',
        body: JSON.stringify({ nmls: nmls.trim() }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipeline'] });
      qc.invalidateQueries({ queryKey: ['pipeline-stats'] });
      qc.invalidateQueries({ queryKey: ['pipeline-facets'] });
      // refresh the /api/me cache so the header shows the new NMLS
      qc.invalidateQueries({ queryKey: ['me'] });
      // best-effort full reload of auth context: refetch /api/me via the auth hook
      window.location.reload();
    },
  });
  return (
    <div className="surface-card flex flex-wrap items-center gap-3 border-l-4 border-brand-gold-500 bg-brand-gold-50/40 px-5 py-4">
      <div className="flex-1">
        <div className="text-sm font-semibold text-brand-navy-800">
          Set your NMLS to see your loans
        </div>
        <p className="mt-0.5 text-xs text-slate-600">
          The <strong>In Progress</strong> and <strong>Other</strong> tabs filter by your NMLS
          number. Enter it once and we&apos;ll remember it. Admins can also set this for other
          users from the Administration page.
        </p>
      </div>
      <input
        type="text"
        inputMode="numeric"
        value={nmls}
        onChange={(e) => setNmls(e.target.value.replace(/\D/g, ''))}
        placeholder="e.g. 1234567"
        className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-brand-navy-400 focus:outline-none focus:ring-2 focus:ring-brand-navy-200"
      />
      <Button onClick={() => save.mutate()} disabled={!nmls || save.isPending}>
        {save.isPending ? 'Saving…' : 'Save NMLS'}
      </Button>
    </div>
  );
}

/**
 * Admin-only modal for creating a manual loan. Captures the minimum fields
 * the Pipeline UI renders; UWM will overwrite these on next sync if the same
 * (lender, loanNumber) appears in the API response.
 */
function NewLoanModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [loanNumber, setLoanNumber] = useState('');
  const [lender, setLender] = useState('UWM');
  const [borrower1FullName, setBorrower1FullName] = useState('');
  const [loanOfficer, setLoanOfficer] = useState('');
  const [propertyAddress, setPropertyAddress] = useState('');
  const [loanStatus, setLoanStatus] = useState('');
  const [lockStatus, setLockStatus] = useState<'Locked' | 'Not Locked' | 'Unknown'>('Unknown');
  const [loanPurpose, setLoanPurpose] = useState('');
  const [outstandingCondition, setOutstandingCondition] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api('/api/admin/loans', {
        method: 'POST',
        body: JSON.stringify({
          loanNumber,
          lender,
          loanOfficer: loanOfficer || undefined,
          propertyAddress: propertyAddress || undefined,
          loanStatus: loanStatus || undefined,
          lockStatus,
          loanPurpose: loanPurpose || undefined,
          outstandingCondition: outstandingCondition === '' ? undefined : Number(outstandingCondition),
          borrower1FullName: borrower1FullName || undefined,
        }),
      }),
    onSuccess: onCreated,
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 shadow-floating">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="font-display text-xl font-bold text-brand-navy-800">
              New manual loan
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Stored with <code className="font-mono">source=manual</code>. UWM sync will overwrite
              these fields if a loan with the same lender + loan number appears in the API.
            </p>
          </div>
          <Button variant="ghost" onClick={onClose}>
            ✕
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Loan number">
            <Input value={loanNumber} onChange={(e) => setLoanNumber(e.target.value)} required />
          </Field>
          <Field label="Lender">
            <Input value={lender} onChange={(e) => setLender(e.target.value)} />
          </Field>
          <Field label="Borrower 1 full name">
            <Input value={borrower1FullName} onChange={(e) => setBorrower1FullName(e.target.value)} />
          </Field>
          <Field label="Loan officer">
            <Input value={loanOfficer} onChange={(e) => setLoanOfficer(e.target.value)} />
          </Field>
          <Field label="Property address">
            <Input value={propertyAddress} onChange={(e) => setPropertyAddress(e.target.value)} />
          </Field>
          <Field label="Loan status">
            <Input value={loanStatus} onChange={(e) => setLoanStatus(e.target.value)} placeholder="e.g. File Imported" />
          </Field>
          <Field label="Lock status">
            <Select value={lockStatus} onChange={(e) => setLockStatus(e.target.value as 'Locked' | 'Not Locked' | 'Unknown')}>
              <option value="Unknown">Unknown</option>
              <option value="Locked">Locked</option>
              <option value="Not Locked">Not Locked</option>
            </Select>
          </Field>
          <Field label="Purpose">
            <Input value={loanPurpose} onChange={(e) => setLoanPurpose(e.target.value)} placeholder="Purchase / Refinance" />
          </Field>
          <Field label="Outstanding conditions">
            <Input
              type="number"
              min={0}
              value={outstandingCondition}
              onChange={(e) => setOutstandingCondition(e.target.value)}
            />
          </Field>
        </div>

        {err && (
          <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            {err}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!loanNumber.trim() || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Creating…' : 'Create loan'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PipelineFooter({
  page,
  pageSize,
  total,
  loading,
  onPage,
  onPageSize,
}: {
  page: number;
  pageSize: number;
  total: number;
  loading: boolean;
  onPage: (n: number) => void;
  onPageSize: (n: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/50 px-5 py-3 text-xs text-slate-600">
      <div className="flex items-center gap-2">
        <span>
          Showing <strong className="text-brand-navy-800">{first.toLocaleString()}</strong>–
          <strong className="text-brand-navy-800">{last.toLocaleString()}</strong> of{' '}
          <strong className="text-brand-navy-800">{total.toLocaleString()}</strong>
        </span>
        {loading && <span className="text-slate-400">· refreshing…</span>}
      </div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-slate-500">Rows</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSize(Number(e.target.value))}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs shadow-sm focus:border-brand-navy-500 focus:outline-none"
          >
            {[25, 50, 100, 200].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="secondary" disabled={!canPrev} onClick={() => onPage(1)}>
            ⏮
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={!canPrev}
            onClick={() => onPage(page - 1)}
          >
            ← Prev
          </Button>
          <span className="px-2 text-[11px] uppercase tracking-wider text-slate-500">
            Page {page.toLocaleString()} of {totalPages.toLocaleString()}
          </span>
          <Button
            size="sm"
            variant="secondary"
            disabled={!canNext}
            onClick={() => onPage(page + 1)}
          >
            Next →
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={!canNext}
            onClick={() => onPage(totalPages)}
          >
            ⏭
          </Button>
        </div>
      </div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'info' | 'success' | 'warning' | 'gold';
}) {
  const accent = {
    info: 'text-brand-navy-700',
    success: 'text-emerald-600',
    warning: 'text-amber-600',
    gold: 'text-brand-gold-600',
  }[tone];
  return (
    <div className="surface-card px-5 py-4">
      <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">
        {label}
      </div>
      <div className={`mt-1 font-display text-3xl font-bold ${accent}`}>{value}</div>
    </div>
  );
}

function statusTone(status: string | null): 'neutral' | 'info' | 'warning' | 'success' | 'gold' {
  if (!status) return 'neutral';
  const s = status.toLowerCase();
  if (s.includes('cleared') || s.includes('funded') || s.includes('closed')) return 'success';
  if (s.includes('approved')) return 'gold';
  if (s.includes('underwriting') || s.includes('uw')) return 'info';
  if (s.includes('import') || s.includes('set up')) return 'neutral';
  return 'neutral';
}

interface LoanDrawerProps {
  loan: PipelineLoan;
  contacts: Contact[];
  canEdit: boolean;
  isAdmin: boolean;
  onClose: () => void;
  onAfterDelete: () => void;
}

const LoanDrawer = memo(function LoanDrawerInner({
  loan,
  contacts,
  canEdit,
  isAdmin,
  onClose,
  onAfterDelete,
}: LoanDrawerProps) {
  const qc = useQueryClient();

  // Admin-only delete. UWM-sourced rows will be re-pulled on next sync
  // (the server returns `willRehydrateOnSync: true`); manual rows are gone
  // for good unless re-created.
  const deleteLoan = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; willRehydrateOnSync: boolean }>(`/api/admin/loans/${loan.id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipeline'] });
      qc.invalidateQueries({ queryKey: ['pipeline-stats'] });
      onAfterDelete();
    },
  });
  const [assignTo, setAssignTo] = useState<string>(loan.assignedToContactId ?? '');
  const [title, setTitle] = useState(loan.titleCompanyEmail ?? '');
  const [emailSubject, setEmailSubject] = useState(`Action needed: Loan ${loan.loanNumber}`);
  const [emailBody, setEmailBody] = useState(
    `Hi ${loan.assignedToContact?.name ?? ''},\n\nPlease review loan ${loan.loanNumber}.\n`,
  );
  const [waLinkUrl, setWaLinkUrl] = useState<string | null>(null);
  const [reachError, setReachError] = useState<string | null>(null);
  const [reachSuccess, setReachSuccess] = useState<string | null>(null);

  const assignMutation = useMutation({
    mutationFn: (contactId: string | null) =>
      api(`/api/pipeline/loans/${loan.id}/assignment`, {
        method: 'PATCH',
        body: JSON.stringify({ assignedToContactId: contactId }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipeline'] }),
  });
  const titleMutation = useMutation({
    mutationFn: (email: string | null) =>
      api(`/api/pipeline/loans/${loan.id}/title-company`, {
        method: 'PATCH',
        body: JSON.stringify({ titleCompanyEmail: email }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipeline'] }),
  });

  const sendEmail = async () => {
    setReachError(null);
    setReachSuccess(null);
    if (!loan.assignedToContact?.email) {
      setReachError('Assigned contact has no email');
      return;
    }
    try {
      await api('/api/reach/email', {
        method: 'POST',
        body: JSON.stringify({
          loanId: loan.id,
          to: loan.assignedToContact.email,
          subject: emailSubject,
          text: emailBody,
        }),
      });
      setReachSuccess('Email sent.');
    } catch (e) {
      setReachError(e instanceof Error ? e.message : 'Send failed');
    }
  };

  const buildWhatsAppLink = async () => {
    setReachError(null);
    setWaLinkUrl(null);
    const phone = loan.assignedToContact?.whatsappPhone ?? loan.assignedToContact?.phone ?? '';
    if (!phone) {
      setReachError('Assigned contact has no phone');
      return;
    }
    try {
      const r = await api<{ url: string }>('/api/reach/whatsapp-link', {
        method: 'POST',
        body: JSON.stringify({ phone, message: emailBody }),
      });
      setWaLinkUrl(r.url);
    } catch (e) {
      setReachError(e instanceof Error ? e.message : 'Link build failed');
    }
  };

  /**
   * Real outbound WhatsApp send via the org's configured Twilio integration.
   * Uses the same body the user typed in the email composer so a single field
   * drives both channels. Surfaces Twilio's reported delivery status so the
   * test result tells the truth (sandbox/paid: queued vs delivered vs
   * failed).
   */
  const sendWhatsApp = async () => {
    setReachError(null);
    setReachSuccess(null);
    setWaLinkUrl(null);
    const phone = loan.assignedToContact?.whatsappPhone ?? loan.assignedToContact?.phone ?? '';
    if (!phone) {
      setReachError('Assigned contact has no phone');
      return;
    }
    if (!emailBody.trim()) {
      setReachError('Message body is empty');
      return;
    }
    try {
      const r = await api<{
        ok: boolean;
        mode: string;
        providerMessageId: string | null;
        providerStatus: string | null;
        manualUrl: string | null;
        error: string | null;
      }>('/api/reach/whatsapp-send', {
        method: 'POST',
        body: JSON.stringify({
          loanId: loan.id,
          to: phone,
          message: emailBody,
          recipientName: loan.assignedToContact?.name,
        }),
      });
      if (r.mode === 'manual' && r.manualUrl) {
        setWaLinkUrl(r.manualUrl);
        setReachSuccess('Manual mode: opened wa.me link below (no Twilio integration is configured).');
      } else if (r.ok) {
        const status = r.providerStatus ? ` (Twilio status: ${r.providerStatus})` : '';
        setReachSuccess(`WhatsApp sent${status}.`);
      } else {
        setReachError(r.error ?? 'Send failed');
      }
    } catch (e) {
      setReachError(e instanceof Error ? e.message : 'Send failed');
    }
  };

  return (
    // Split structure on purpose:
    //   - backdrop is a separate element so it doesn't re-render with the panel
    //   - no backdrop-filter: backdrop-blur on Windows GPUs causes per-frame
    //     repaints of the entire blurred region during fast keystrokes, which
    //     manifests as the "flicker" the user reported.
    <>
      <div
        className="fixed inset-0 z-30 bg-slate-900/40"
        onClick={onClose}
        aria-hidden
      />
      <aside className="fixed inset-y-0 right-0 z-40 flex h-full w-full max-w-2xl flex-col overflow-y-auto bg-white shadow-floating">
        <header className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-200 bg-white px-6 py-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-gold-700">
              Loan detail
            </p>
            <div className="font-display text-2xl font-bold text-brand-navy-800">
              {loan.loanNumber}
            </div>
            <div className="text-sm text-slate-500">{loan.propertyAddress ?? '—'}</div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              ✕ Close
            </Button>
            {isAdmin && (
              <Button
                size="sm"
                variant="danger"
                disabled={deleteLoan.isPending}
                title="Permanently delete this loan row. If the loan still exists in UWM, the next sync will re-create it automatically."
                onClick={() => {
                  const msg =
                    `Delete loan ${loan.loanNumber}?\n\n` +
                    `If this loan is still in UWM, the next sync will re-create it with the latest data. ` +
                    `Manual loans are gone permanently.`;
                  if (confirm(msg)) deleteLoan.mutate();
                }}
              >
                {deleteLoan.isPending ? 'Deleting…' : 'Delete loan'}
              </Button>
            )}
          </div>
        </header>

        <div className="space-y-6 px-6 py-6 text-sm">
          <section>
            <SectionTitle>Parties</SectionTitle>
            <ul className="mt-2 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
              {loan.parties.map((p) => (
                <li key={p.role} className="px-4 py-3">
                  <div className="font-medium text-brand-navy-800">{p.fullName}</div>
                  <div className="mt-0.5 text-xs uppercase tracking-wider text-slate-400">
                    {p.role.replace('_', ' ')}
                  </div>
                  <div className="mt-1 text-xs text-slate-600">
                    {p.email ?? '—'} · {p.phone ?? '—'}
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="space-y-3">
            <SectionTitle>Assignment</SectionTitle>
            <Field label="Assigned to">
              <Select
                value={assignTo}
                onChange={(e) => setAssignTo(e.target.value)}
                disabled={!canEdit}
              >
                <option value="">— None —</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.contactType})
                  </option>
                ))}
              </Select>
            </Field>
            <Button
              disabled={!canEdit || assignMutation.isPending}
              onClick={() => assignMutation.mutate(assignTo || null)}
            >
              Save assignment
            </Button>
          </section>

          <section className="space-y-3">
            <SectionTitle>Title company</SectionTitle>
            <Field label="Title company email">
              <Input
                type="email"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={!canEdit}
              />
            </Field>
            <Button
              variant="secondary"
              disabled={!canEdit || titleMutation.isPending}
              onClick={() => titleMutation.mutate(title || null)}
            >
              Save title email
            </Button>
            {titleMutation.error && (
              <div className="text-xs text-rose-600">
                {(titleMutation.error as Error).message}
              </div>
            )}
          </section>

          <section className="space-y-3">
            <SectionTitle>Reach assigned contact</SectionTitle>
            <Field label="Subject">
              <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
            </Field>
            <Field label="Message">
              <textarea
                rows={4}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-navy-500 focus:outline-none focus:ring-2 focus:ring-brand-navy-200"
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
              />
            </Field>
            <div className="flex flex-wrap gap-2">
              <Button disabled={!canEdit} onClick={sendEmail}>
                Send email
              </Button>
              <Button
                disabled={!canEdit}
                variant="gold"
                onClick={sendWhatsApp}
                title="Send a real WhatsApp via your Twilio integration"
              >
                Send WhatsApp
              </Button>
              <Button variant="secondary" onClick={buildWhatsAppLink}>
                Or build wa.me link
              </Button>
            </div>
            {waLinkUrl && (
              <a
                href={waLinkUrl}
                target="_blank"
                rel="noreferrer"
                className="block break-all rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700 hover:bg-emerald-100"
              >
                ↗ {waLinkUrl}
              </a>
            )}
            {reachSuccess && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                {reachSuccess}
              </div>
            )}
            {reachError && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {reachError}
              </div>
            )}
          </section>

          <WorkflowActivitySection loanId={loan.id} />

          <section>
            <SectionTitle>Closing milestones</SectionTitle>
            <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 rounded-xl border border-slate-200 bg-white p-3 text-xs">
              <DateRow label="E-Sign completed" flag={loan.eSignCompletedFlag} />
              <DateRow label="Estimated closing" date={loan.estimatedClosingDate} />
              <DateRow label="Closing" date={loan.closingDateTime} />
              <DateRow label="Closing package received" date={loan.closingPackageReceivedDate} />
              <DateRow label="Disbursement" date={loan.disbursementDate} />
              <DateRow label="EPO (early payoff)" date={loan.epoDate} />
              <DateRow label="CD signed" flag={loan.disclosures?.cdSignedFlag ?? null} />
            </dl>
          </section>

          <section>
            <SectionTitle>UWM underwriters</SectionTitle>
            <p className="mt-1 text-xs text-slate-500">
              UWM Insights exposes Senior UW and UW2 contact info, not detailed condition lists.
            </p>
          </section>

          <section>
            <SectionTitle>Manual entry only</SectionTitle>
            <p className="mt-1 text-xs text-slate-500">
              UWM Insights does not expose Borrower 2/3/4 phone or email, Non-Borrowing Spouse, or
              Non-Borrowing Individual data. These remain manual-entry / future-connector fields
              and won&apos;t populate from sync.
            </p>
          </section>
        </div>
      </aside>
    </>
  );
});

// ===========================================================================
// Workflow activity panel — surfaces NotificationLog rows for the loan so
// authors can see exactly why a workflow action was skipped/failed, and
// confirm WhatsApp / email actually went out the door.
// ===========================================================================

interface LoanDetailNotification {
  id: string;
  workflowInstanceId: string | null;
  stepKey: string | null;
  templateKey: string | null;
  channel: string;
  recipient: string;
  subject: string | null;
  message: string;
  status: string;
  providerResponse: string | null;
  error: string | null;
  createdAt: string;
}

interface LoanDetailResponse {
  loan: {
    id: string;
    notificationLogs: LoanDetailNotification[];
  };
}

function WorkflowActivitySection({ loanId }: { loanId: string }) {
  const detail = useQuery<LoanDetailResponse>({
    queryKey: ['pipeline', 'loan-detail', loanId],
    queryFn: () => api<LoanDetailResponse>(`/api/pipeline/loans/${loanId}`),
    staleTime: 30_000,
  });
  const logs = detail.data?.loan.notificationLogs ?? [];
  const channelTone = (channel: string): React.ComponentProps<typeof Badge>['tone'] => {
    if (channel === 'email') return 'info';
    if (channel === 'whatsapp') return 'success';
    if (channel === 'sms') return 'warning';
    return 'neutral';
  };
  const statusTone = (status: string): React.ComponentProps<typeof Badge>['tone'] => {
    if (status === 'sent') return 'success';
    if (status === 'failed') return 'danger';
    if (status === 'queued') return 'info';
    return 'warning';
  };

  return (
    <section>
      <SectionTitle>Workflow activity</SectionTitle>
      <p className="mt-1 text-xs text-slate-500">
        Every notification this loan has triggered, newest first. Status &
        error tell you exactly why a message did or didn&apos;t go out.
      </p>
      {detail.isLoading && (
        <p className="mt-2 text-xs text-slate-500">Loading activity…</p>
      )}
      {!detail.isLoading && logs.length === 0 && (
        <p className="mt-2 rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-500">
          No notifications recorded for this loan yet. If a published workflow
          should have fired, the worker may not have evaluated this loan since
          the workflow was published — wait until the next trigger fire, or
          run a sync.
        </p>
      )}
      {logs.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {logs.map((n) => (
            <li
              key={n.id}
              className="rounded-lg border border-slate-200 bg-white p-2.5 text-xs"
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge tone={channelTone(n.channel)}>{n.channel}</Badge>
                <Badge tone={statusTone(n.status)}>{n.status}</Badge>
                {n.stepKey && (
                  <span className="font-mono text-[10px] text-slate-500">
                    {n.stepKey}
                  </span>
                )}
                <span className="ml-auto text-[10px] text-slate-400">
                  {new Date(n.createdAt).toLocaleString()}
                </span>
              </div>
              <div className="mt-1 text-slate-700">
                <span className="text-[10px] uppercase tracking-wider text-slate-400">
                  to:
                </span>{' '}
                <span className="font-mono">{n.recipient}</span>
              </div>
              {n.subject && (
                <div className="mt-0.5 text-slate-700">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">
                    subject:
                  </span>{' '}
                  {n.subject}
                </div>
              )}
              {n.error && (
                <div className="mt-1 rounded bg-rose-50 px-2 py-1 text-rose-700 ring-1 ring-rose-200">
                  <span className="text-[10px] font-semibold uppercase tracking-wider">
                    error:
                  </span>{' '}
                  <span className="font-mono">{n.error}</span>
                </div>
              )}
              {n.providerResponse && (
                <div className="mt-1 text-[10px] text-slate-500">
                  provider id: <span className="font-mono">{n.providerResponse}</span>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DateRow({
  label,
  date,
  flag,
}: {
  label: string;
  date?: string | null;
  flag?: 'Y' | 'N' | string | null;
}) {
  let value: React.ReactNode;
  if (typeof flag !== 'undefined') {
    value =
      flag === 'Y' ? (
        <span className="text-emerald-700">Yes</span>
      ) : flag === 'N' ? (
        <span className="text-slate-500">No</span>
      ) : (
        <span className="text-slate-400">—</span>
      );
  } else if (date) {
    value = <span className="text-slate-800">{new Date(date).toLocaleDateString()}</span>;
  } else {
    value = <span className="text-slate-400">—</span>;
  }
  return (
    <>
      <dt className="text-[11px] uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </>
  );
}
